// src/components/classroom/ClassroomControls.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  FaMicrophone,
  FaMicrophoneSlash,
  FaVideo,
  FaVideoSlash,
  FaDesktop,
  FaHandPaper,
  FaComments,
  FaUsers,
  FaSignOutAlt,
  FaCog,
} from "react-icons/fa";
import { useConnectionState, useLocalParticipant } from "@livekit/components-react";
import { ConnectionState } from "livekit-client";
import { toast } from "react-toastify";
import { setVerifiedMicrophoneEnabled } from "./microphonePublication";
import MicrophoneSettings from "./MicrophoneSettings";

export default function ClassroomControls({
  credentials,
  onLeave,
  onToggleChat,
  onToggleParticipants,
  showChat = false,
  showParticipants = false,
  participantCount = 1,
  initialMicEnabled = true,
  sendClassroomEvent,
  onLocalHandChange,
}) {
  const { isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled, localParticipant } =
    useLocalParticipant();
  const connectionState = useConnectionState();

  const [handRaised, setHandRaised] = useState(false);
  const [screenSharePending, setScreenSharePending] = useState(false);
  const [micToggling, setMicToggling] = useState(false);
  const [showMicrophoneSettings, setShowMicrophoneSettings] = useState(false);

  const allowedSources = credentials?.allowedPublishSources || [];
  const canUseCamera = allowedSources.includes("camera");
  const canUseMicrophone = allowedSources.includes("microphone");
  const canScreenShare = allowedSources.includes("screen_share");
  const browserCanScreenShare = typeof navigator !== "undefined"
    && Boolean(navigator.mediaDevices?.getDisplayMedia);

  // Single-shot auto-activation of microphone on initial room connection if intended
  const autoActivatedRef = useRef(false);
  useEffect(() => {
    if (autoActivatedRef.current) return;
    if (
      connectionState === ConnectionState.Connected &&
      localParticipant &&
      canUseMicrophone &&
      initialMicEnabled
    ) {
      autoActivatedRef.current = true;
      setMicToggling(true);
      setVerifiedMicrophoneEnabled(localParticipant, true).then(({ mediaTrack }) => {
        console.info("[ClassroomControls] Microphone published", {
          label: mediaTrack.label,
          readyState: mediaTrack.readyState,
          enabled: mediaTrack.enabled,
        });
      }).catch((err) => {
        console.error("[ClassroomControls] Initial microphone activation failed:", err);
        toast.error(`Microphone could not start: ${err?.message || "Select a microphone and try again."}`);
      }).finally(() => {
        setMicToggling(false);
      });
    }
  }, [connectionState, localParticipant, canUseMicrophone, initialMicEnabled]);

  const toggleMic = async () => {
    if (!canUseMicrophone) {
      toast.warn("Microphone publishing is restricted by the instructor.");
      return;
    }
    if (micToggling) return;
    setMicToggling(true);
    try {
      if (localParticipant) {
        const nextState = !isMicrophoneEnabled;
        const { publication, mediaTrack } = await setVerifiedMicrophoneEnabled(
          localParticipant,
          nextState
        );

        if (nextState) {
          console.info("[ClassroomControls] Microphone re-enabled", {
            publicationSid: publication?.trackSid,
            label: mediaTrack?.label,
            readyState: mediaTrack?.readyState,
          });
          toast.success("Microphone is active. You can speak now.");
        } else {
          toast.info("Microphone muted.");
        }
      }
    } catch (err) {
      console.error("[ClassroomControls] Microphone error:", err);
      toast.error(`Microphone error: ${err?.message || "Could not toggle microphone."}`);
    } finally {
      setMicToggling(false);
    }
  };

  const toggleCam = async () => {
    if (!canUseCamera) {
      toast.warn("Camera publishing is restricted for this session.");
      return;
    }
    try {
      if (localParticipant) {
        await localParticipant.setCameraEnabled(!isCameraEnabled);
      }
    } catch (err) {
      toast.error(`Camera error: ${err.message}`);
    }
  };

  const toggleScreen = async () => {
    if (!canScreenShare) {
      toast.warn("Screen sharing is restricted to faculty hosts.");
      return;
    }
    if (!browserCanScreenShare) {
      toast.warn("Screen sharing is not supported by this browser or device. Try the latest desktop Chrome, Edge, or Firefox.");
      return;
    }
    if (screenSharePending) return;
    setScreenSharePending(true);
    try {
      if (localParticipant) {
        await localParticipant.setScreenShareEnabled(!isScreenShareEnabled);
      }
    } catch (err) {
      const errorName = String(err?.name || "").toLowerCase();
      const errorMessage = String(err?.message || "").toLowerCase();
      const permissionWasDeclined = errorName === "notallowederror"
        || errorName === "permissiondeniederror"
        || errorMessage.includes("permission denied by user")
        || errorMessage.includes("permission dismissed")
        || errorMessage.includes("permission denied");

      if (permissionWasDeclined) {
        toast.info("Screen sharing was cancelled. Click Share Screen again and select a screen, window, or browser tab.");
      } else if (errorName === "notreadableerror" || errorMessage.includes("could not start")) {
        toast.error("The selected screen cannot be captured. Close other screen-sharing apps and try again.");
      } else {
        toast.error(`Unable to start screen sharing${err?.message ? `: ${err.message}` : "."}`);
      }
    } finally {
      setScreenSharePending(false);
    }
  };

  const toggleHand = async () => {
    const next = !handRaised;
    try {
      await sendClassroomEvent?.({
        type: "HAND",
        raised: next,
        identity: localParticipant?.identity,
        sender: localParticipant?.name || localParticipant?.identity || "Participant",
      });
      setHandRaised(next);
      onLocalHandChange?.(localParticipant?.identity, next);
      toast.info(next ? "Hand raised to ask a question." : "Hand lowered.");
    } catch (error) {
      toast.error(`Unable to update hand status: ${error?.message || "Connection unavailable."}`);
    }
  };

  return (
    <div className="flex w-full items-center justify-around gap-1 overflow-x-auto sm:w-auto sm:justify-start sm:gap-2">
      <MicrophoneSettings open={showMicrophoneSettings} onClose={() => setShowMicrophoneSettings(false)} />
      {/* Microphone Control */}
      {canUseMicrophone ? (
        <button
          type="button"
          onClick={toggleMic}
          disabled={micToggling}
          className={`rounded-full p-2.5 text-xs transition ${
            isMicrophoneEnabled
              ? "bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700"
              : "bg-rose-600 text-white hover:bg-rose-700 shadow-md shadow-rose-600/30"
          } disabled:cursor-wait disabled:opacity-60`}
          title={isMicrophoneEnabled ? "Mute Microphone" : "Unmute Microphone"}
        >
          {isMicrophoneEnabled ? <FaMicrophone /> : <FaMicrophoneSlash />}
        </button>
      ) : (
        <button
          type="button"
          onClick={toggleMic}
          className="rounded-full bg-slate-800/50 p-2.5 text-xs text-slate-600 cursor-not-allowed border border-slate-800"
          title="Microphone disabled by host"
          aria-label="Microphone disabled by instructor"
        >
          <FaMicrophoneSlash />
        </button>
      )}

      {canUseMicrophone && (
        <button
          type="button"
          onClick={() => setShowMicrophoneSettings(true)}
          className="rounded-full border border-slate-700 bg-slate-800 p-2.5 text-xs text-slate-300 transition hover:bg-slate-700 hover:text-white"
          title="Choose and test microphone"
          aria-label="Choose and test microphone"
        >
          <FaCog />
        </button>
      )}

      {/* Camera Control */}
      {canUseCamera ? (
        <button
          type="button"
          onClick={toggleCam}
          className={`rounded-full p-2.5 text-xs transition ${
            isCameraEnabled
              ? "bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700"
              : "bg-rose-600 text-white hover:bg-rose-700 shadow-md shadow-rose-600/30"
          }`}
          title={isCameraEnabled ? "Turn Off Camera" : "Turn On Camera"}
        >
          {isCameraEnabled ? <FaVideo /> : <FaVideoSlash />}
        </button>
      ) : (
        <button
          type="button"
          onClick={toggleCam}
          className="rounded-full bg-slate-800/50 p-2.5 text-xs text-slate-600 cursor-not-allowed border border-slate-800"
          title="Camera disabled by host"
          aria-label="Camera disabled by instructor"
        >
          <FaVideoSlash />
        </button>
      )}

      {/* Screen Share Control (Host Only) */}
      {canScreenShare && (
        <button
          type="button"
          onClick={toggleScreen}
          disabled={screenSharePending || !browserCanScreenShare}
          className={`rounded-full p-2.5 text-xs transition ${
            isScreenShareEnabled
              ? "bg-sky-500 text-white"
              : "bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700"
          } disabled:cursor-not-allowed disabled:opacity-50`}
          title={
            !browserCanScreenShare
              ? "Screen sharing is unavailable on this browser or device"
              : screenSharePending
              ? "Waiting for screen selection"
              : isScreenShareEnabled ? "Stop Screen Share" : "Share Screen"
          }
        >
          <FaDesktop className={screenSharePending ? "animate-pulse" : ""} />
        </button>
      )}

      {/* Hand Raise */}
      <button
        type="button"
        onClick={toggleHand}
        className={`rounded-full p-2.5 text-xs transition ${
          handRaised
            ? "bg-amber-500 text-slate-950 font-bold"
            : "bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700"
        }`}
        title={handRaised ? "Lower Hand" : "Raise Hand"}
      >
        <FaHandPaper />
      </button>

      {/* Participants Toggle */}
      {onToggleParticipants && (
        <button
          type="button"
          onClick={onToggleParticipants}
          className={`rounded-full p-2.5 text-xs transition relative ${
            showParticipants
              ? "bg-sky-500 text-white"
              : "bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700"
          }`}
          title="Toggle Participants"
        >
          <FaUsers />
          <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-[9px] font-bold text-white">
            {participantCount}
          </span>
        </button>
      )}

      {/* Chat Toggle */}
      {onToggleChat && (
        <button
          type="button"
          onClick={onToggleChat}
          className={`rounded-full p-2.5 text-xs transition relative ${
            showChat
              ? "bg-sky-500 text-white"
              : "bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700"
          }`}
          title="Toggle Chat"
        >
          <FaComments />
        </button>
      )}

      {/* Leave Button */}
      <button
        type="button"
        onClick={onLeave}
        className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-2 text-xs font-bold text-white hover:bg-rose-700 transition shadow-lg shadow-rose-600/30"
      >
        <FaSignOutAlt /> Leave Class
      </button>
    </div>
  );
}
